import { useEffect, useState } from 'react';
import { FolderKanban, X } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import type { ProjectPrivacy } from '../../types';

const PROJECT_COLORS = ['#6366f1', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#a855f7'];

export default function CreateProjectModal() {
    const {
        showCreateProject, setShowCreateProject, createProject, setActiveView,
        departments, users, currentUser
    } = useApp();
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [color, setColor] = useState(PROJECT_COLORS[0]);
    const [privacy, setPrivacy] = useState<ProjectPrivacy>('private');
    const [departmentId, setDepartmentId] = useState('');
    const [memberIds, setMemberIds] = useState<string[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!showCreateProject) return;
        setName('');
        setDescription('');
        setColor(PROJECT_COLORS[0]);
        setPrivacy('private');
        setDepartmentId(currentUser?.department_id || '');
        setMemberIds([]);
        setError('');
    }, [showCreateProject, currentUser?.department_id]);

    if (!showCreateProject) return null;

    const close = () => {
        if (!isSubmitting) setShowCreateProject(false);
    };

    const submit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!name.trim() || isSubmitting) return;
        setIsSubmitting(true);
        setError('');
        try {
            const project = await createProject({
                name: name.trim(), description, color, privacy,
                department_id: privacy === 'department' ? departmentId : null,
                item_visibility: 'project_members', member_ids: memberIds
            });
            setShowCreateProject(false);
            setActiveView({ type: 'ticket-board', id: project.id });
        } catch (createError: any) {
            setError(createError?.message || 'Could not create the project.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={close}>
            <div className="modal" style={{ maxWidth: 560 }} onClick={event => event.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="create-project-title">
                <header className="modal-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 34, height: 34, borderRadius: 9, background: `${color}22`, color, display: 'grid', placeItems: 'center' }}>
                            <FolderKanban size={19} />
                        </div>
                        <div>
                            <h2 className="modal-title" id="create-project-title">Create project</h2>
                            <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>Organize related work on one shared board.</div>
                        </div>
                    </div>
                    <button type="button" className="btn btn-ghost btn-icon" onClick={close} aria-label="Close"><X size={18} /></button>
                </header>
                <form onSubmit={submit}>
                    <div className="modal-body">
                        {error && <div className="auth-error" role="alert">{error}</div>}
                        <div className="form-group">
                            <label className="form-label" htmlFor="project-name">Project name *</label>
                            <input id="project-name" className="input" autoFocus maxLength={150} value={name} onChange={event => setName(event.target.value)} placeholder="e.g. Website launch" />
                        </div>
                        <div className="form-group">
                            <label className="form-label" htmlFor="project-description">Description</label>
                            <textarea id="project-description" className="input textarea" rows={3} value={description} onChange={event => setDescription(event.target.value)} placeholder="What is this project for?" />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Color</label>
                            <div style={{ display: 'flex', gap: 9 }}>
                                {PROJECT_COLORS.map(option => (
                                    <button key={option} type="button" onClick={() => setColor(option)} aria-label={`Use ${option}`} aria-pressed={color === option}
                                        style={{ width: 28, height: 28, borderRadius: 8, background: option, border: color === option ? '3px solid var(--text-primary)' : '2px solid transparent', cursor: 'pointer' }} />
                                ))}
                            </div>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                            <div className="form-group">
                                <label className="form-label" htmlFor="project-privacy">Visibility</label>
                                <select id="project-privacy" className="input select" value={privacy} onChange={event => setPrivacy(event.target.value as ProjectPrivacy)}>
                                    <option value="private">Private — invited members</option>
                                    <option value="department">Department</option>
                                    <option value="organization">Organization</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label className="form-label" htmlFor="project-department">Department</label>
                                <select id="project-department" className="input select" value={departmentId} disabled={privacy !== 'department'} onChange={event => setDepartmentId(event.target.value)} required={privacy === 'department'}>
                                    <option value="">Select department</option>
                                    {departments.map(department => <option key={department.id} value={department.id}>{department.name}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="form-group">
                            <label className="form-label" htmlFor="project-members">Initial members</label>
                            <select id="project-members" className="input" multiple size={4} value={memberIds}
                                onChange={event => setMemberIds(Array.from(event.target.selectedOptions, option => option.value))}>
                                {users.filter(user => user.id !== currentUser?.id).map(user => <option key={user.id} value={user.id}>{user.name}</option>)}
                            </select>
                            <div style={{ marginTop: 5, color: 'var(--text-muted)', fontSize: 11 }}>Use Ctrl/Cmd to select several people. Initial members start as viewers.</div>
                        </div>
                    </div>
                    <footer className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={close}>Cancel</button>
                        <button type="submit" className="btn btn-primary" disabled={!name.trim() || isSubmitting}>{isSubmitting ? 'Creating…' : 'Create project'}</button>
                    </footer>
                </form>
            </div>
        </div>
    );
}
